import apiInstance from '@api';
import { SubmitDemoRequestPayload } from '@api/support/SupportAPI';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import FormFieldsContainer from '../../../Form/components/FormFieldContainer';
import SchemaRenderer from '../../../Form/components/SchemaRenderer';
import toastMessage from '../../../Toast';

import {
  DATE_RANGE_ERROR_TEXT,
  DEMO_DEFAULT_VALUES,
  DEMO_REQUEST_TYPE,
} from '../../constants';
import { DemoRequestFormValues, SupportFormProps } from '../../types';
import {
  buildDemoRequestFormSchema,
  formatDemoDateRange,
  getOptionValue,
  parseEmails,
} from '../../utils';

import FormActions from '../FormActions';

// Demo requests are emailed to the support mailbox, like feedback.
const DemoRequestForm: FC<SupportFormProps> = ({ onCancel, onSubmitted }) => {
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(() => buildDemoRequestFormSchema(), []);
  const methods = useForm<DemoRequestFormValues>({
    mode: 'onSubmit',
    defaultValues: DEMO_DEFAULT_VALUES,
  });

  const handleSubmit = useCallback(
    async (values: DemoRequestFormValues) => {
      const [startDate, endDate] = values.date_range ?? [null, null];

      // `required` on the picker cannot catch a [null, null] range.
      if (!startDate || !endDate) {
        methods.setError('date_range', {
          type: 'required',
          message: DATE_RANGE_ERROR_TEXT,
        });
        return;
      }

      setSubmitting(true);

      const emails = parseEmails(values.emails);
      const payload: SubmitDemoRequestPayload = {
        request_type: DEMO_REQUEST_TYPE,
        date_range: formatDemoDateRange([startDate, endDate]),
        time_selection: getOptionValue(values.time_selection),
        topic: values.topic.trim(),
        ...(emails.length ? { emails } : {}),
      };

      try {
        const { data: response } =
          await apiInstance.support.submitSupportRequest(payload);

        toastMessage.success(
          response?.data?.message || 'Thank you for your demo request!',
        );
        onSubmitted();
      } catch (error) {
        console.error('Failed to submit the demo request:', error);
        toastMessage.error(
          'Failed to submit your demo request. Please try again later.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [methods, onSubmitted],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormFieldsContainer columns={1}>
          <SchemaRenderer schema={schema} />
        </FormFieldsContainer>
        <FormActions
          submitLabel="Request Demo"
          submitting={submitting}
          onCancel={onCancel}
        />
      </form>
    </FormProvider>
  );
};

export default DemoRequestForm;
